import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface CartLine {
  /**
   * Anúncio específico do marketplace.
   *
   * A peça é única: float, pattern e preço são daquele anúncio, não do
   * desgaste em geral. Comprar "uma Redline FT" sem dizer qual deixaria o
   * cliente esperando uma peça e recebendo outra.
   */
  listingId: string;
  templateId: number;
  name: string;
  iconUrl: string | null;
  exterior: string | null;
  float: number | null;
  paintSeed: number | null;
  /**
   * Link de inspeção no jogo e foto da peça, guardados junto.
   *
   * Vêm do anúncio e são opcionais: sacola montada por uma versão anterior do
   * site não tem os dois campos, e continua válida.
   */
  inspectUrl?: string | null;
  imageUrl?: string | null;
  /** Preço no momento em que foi adicionado — só para exibir na sacola. */
  unitBrl: number;
  qty: number;
}

interface CartState {
  linhas: CartLine[];
  total: number;
  quantidade: number;
  adicionar: (linha: Omit<CartLine, 'qty'>) => void;
  remover: (listingId: string) => void;
  limpar: () => void;
  contem: (listingId: string) => boolean;
}

const CartContext = createContext<CartState | null>(null);
const CHAVE = 'dms:carrinho';

/**
 * Carrinho no navegador.
 *
 * Fica no localStorage e não no servidor: assim quem ainda não entrou pode
 * montar a sacola, e ela sobrevive a um recarregamento. O preço guardado aqui
 * é só para mostrar — no fechamento o servidor recalcula tudo a partir do
 * catálogo, porque preço vindo do navegador não é preço, é sugestão.
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const [linhas, setLinhas] = useState<CartLine[]>(() => {
    try {
      const bruto = localStorage.getItem(CHAVE);
      return bruto ? (JSON.parse(bruto) as CartLine[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE, JSON.stringify(linhas));
    } catch {
      // Armazenamento cheio ou bloqueado: a sacola vale só nesta aba.
    }
  }, [linhas]);

  // Sem quantidade: cada anúncio é uma peça só. Clicar de novo não soma.
  const adicionar = useCallback((linha: Omit<CartLine, 'qty'>) => {
    setLinhas((prev) =>
      prev.some((l) => l.listingId === linha.listingId) ? prev : [...prev, { ...linha, qty: 1 }],
    );
  }, []);

  const remover = useCallback((listingId: string) => {
    setLinhas((prev) => prev.filter((l) => l.listingId !== listingId));
  }, []);

  const limpar = useCallback(() => setLinhas([]), []);

  const value = useMemo<CartState>(
    () => ({
      linhas,
      total: linhas.reduce((s, l) => s + l.unitBrl, 0),
      quantidade: linhas.length,
      adicionar,
      remover,
      limpar,
      contem: (id) => linhas.some((l) => l.listingId === id),
    }),
    [linhas, adicionar, remover, limpar],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart precisa estar dentro de <CartProvider>');
  return ctx;
}
